/**
 * The gallery grid — one neon card per game. Clicking the card face
 * launches; the TOP 10 chip opens the leaderboard modal.
 */

import { CATALOG, type CatalogEntry } from '../catalog.js';
import { track } from '../runtime/analytics.js';
import { openLeaderboard } from './leaderboard.js';
import { launchGame, type PlaySession } from './play.js';
import { resolvePlayAccess } from './play-access.js';
import { consumePendingLaunch, paywallGrantsPlay, showLoginWall, showPaywall } from './play-gate-wall.js';
import { refreshFooterCredits } from './footer-credit.js';
import { featuredIds } from './featured.js';
import { playCountFor } from './plays.js';
import { clearDeepLink, markDeepLink, pendingDeepLink } from './deep-link.js';

let activeSession: PlaySession | null = null;
// Synchronous "a launch is in flight" latch (sc-7064). activeSession alone
// can't guard concurrency because it isn't set until the ASYNC gate
// (resolvePlayAccess → network) resolves — a fast double-click re-enters
// launch() while the first gate promise is still pending, both see
// activeSession===null, and both proceed → double token spend / two
// sessions. This flag is set SYNCHRONOUSLY the moment a launch begins and
// released when the gate ends in a non-play verdict, the session closes,
// or the gate throws.
let launching = false;

const POPULAR_BAND_SIZE = 3;
const NEW_BAND_SIZE = 3;

/**
 * ONE continuous grid. Order carries the meaning and the corner ribbon
 * labels the card — there are no section headings, so the gallery keeps
 * reflowing naturally at every viewport width.
 *
 * Running order:
 *
 *   1-3   FEATURED  — editorial curation from the DB (sc-8136), with
 *                     catalog.ts's FEATURED_ORDER as the offline default.
 *                     See featured.ts for the three-tier resolution.
 *   4-6   POPULAR   — most-played, by playCountFor().
 *   7-9   NEW       — most recent by `released`.
 *   rest            — everything else, by play count.
 *
 * FIRST GROUP WINS: a cartridge appears exactly once. BUNKER-84 is
 * featured AND heavily played, but it sits in the featured run and
 * POPULAR promotes the next title up.
 *
 * The groups are positional, NOT rows: the grid is
 * `repeat(auto-fill, minmax(252px, 1fr))`, so the column count is
 * responsive (2 at 768px, 4 at 1440, 5 at 1920) and a group will often
 * straddle a row boundary. That is fine — the ribbon travels with the
 * card, so a FEATURED title is legible wherever it lands.
 */
function orderedCatalog(): { entry: CatalogEntry; chip: string | null }[] {
  const byLineup = new Map(CATALOG.map((e, i) => [e.id, i]));
  const lineup = (a: CatalogEntry, b: CatalogEntry) =>
    (byLineup.get(a.id) ?? 0) - (byLineup.get(b.id) ?? 0);
  const taken = new Set<string>();
  /**
   * Take up to `limit` entries that no earlier group has claimed.
   *
   * The cap is applied AFTER filtering, which is the whole trick: slicing
   * first lets an already-claimed title eat a slot and silently shrink
   * the group. That is a real bug I caught here — FEATURED took
   * BUNKER-84/NEON GRIND/NEON OVERPASS, then NEW sliced the top 3 by
   * date, two of which were already gone, so NEW yielded 2 cards and
   * POPULAR none at all.
   */
  const claim = (entries: CatalogEntry[], limit = Number.MAX_SAFE_INTEGER) => {
    const out = entries.filter((e) => !taken.has(e.id)).slice(0, limit);
    out.forEach((e) => taken.add(e.id));
    return out;
  };

  // FEATURED — resolved by featured.ts: live DB curation when the API has
  // answered, the last cached response otherwise, and catalog.ts's
  // FEATURED_ORDER as the offline default (sc-8136). Ids arrive already
  // ordered and already filtered against CATALOG.
  const order = featuredIds();
  const byId = new Map(CATALOG.map((e) => [e.id, e]));
  const featured = claim(
    order.map((id) => byId.get(id)).filter((e): e is CatalogEntry => e !== undefined)
  );

  // NEW is claimed BEFORE popular, even though it renders after it.
  //
  // Play counts are global when the API has answered and this device's
  // ledger otherwise (plays.ts) — so for a first-time player on a cold
  // API every count is 0 and the sort collapses to `lineup`, which is
  // newest-first. Claiming popular first therefore handed it exactly the
  // titles NEW wanted: measured on a fresh profile, POPULAR took
  // STORMBREAK + BINGO FUEL and the NEW band disappeared entirely.
  //
  // Recency is knowable offline and never wrong; popularity is not. The
  // certain band claims first, and POPULAR shows the most-played of
  // what remains — which is what "popular" should mean next to a
  // dedicated NEW shelf anyway.
  const fresh = claim(
    [...CATALOG].sort((a, b) => b.released.localeCompare(a.released) || lineup(a, b)),
    NEW_BAND_SIZE
  );

  const popular = claim(
    [...CATALOG].sort((a, b) => playCountFor(b.id) - playCountFor(a.id) || lineup(a, b)),
    POPULAR_BAND_SIZE
  );

  const rest = claim(
    [...CATALOG].sort(
      (a, b) => playCountFor(b.id) - playCountFor(a.id) || lineup(a, b)
    )
  );

  return [
    ...featured.map((entry) => ({ entry, chip: 'FEATURED' as const })),
    ...popular.map((entry) => ({ entry, chip: 'POPULAR' as const })),
    ...fresh.map((entry) => ({ entry, chip: 'NEW' as const })),
    ...rest.map((entry) => ({ entry, chip: null })),
  ];
}

export function renderGrid(): void {
  const grid = document.getElementById('gallery-grid');
  if (!(grid instanceof HTMLElement)) {
    throw new Error('gallery grid DOM missing — index.html is broken');
  }
  grid.textContent = '';

  orderedCatalog().forEach(({ entry, chip }, i) => {
    grid.appendChild(buildCard(entry, i, chip));
  });
}

function buildCard(entry: CatalogEntry, index: number, chip: string | null): HTMLElement {
  const card = document.createElement('article');
  card.className = 'card';
  card.style.setProperty('--card-accent', entry.accent);
  card.style.setProperty('--card-accent2', entry.accent2);
  card.style.setProperty('--card-i', String(index));

  const band = document.createElement('div');
  band.className = 'card__band';

  if (chip) {
    const el = document.createElement('span');
    // Shared ribbon styling; the modifier only varies the palette and the
    // letter-spacing, which has to loosen for the shorter word.
    el.className = `card__chip card__chip--${chip.toLowerCase()}`;
    el.textContent = chip;
    card.appendChild(el);
  }

  // the cabinet screen — a CSS mini-scene unique to each game
  const scene = document.createElement('div');
  scene.className = 'card__scene';
  scene.dataset.scene = entry.id;
  for (let s = 1; s <= 3; s++) {
    const el = document.createElement('i');
    el.className = `sc sc${s}`;
    scene.appendChild(el);
  }
  const glare = document.createElement('span');
  glare.className = 'card__glare';
  scene.appendChild(glare);

  const title = document.createElement('h2');
  title.className = 'card__title';
  title.textContent = entry.title;

  const genre = document.createElement('span');
  genre.className = 'card__genre';
  genre.textContent = `${entry.genre} · 1984`;

  const blurb = document.createElement('p');
  blurb.className = 'card__blurb';
  blurb.textContent = entry.blurb;

  const actions = document.createElement('div');
  actions.className = 'card__actions';

  const playBtn = document.createElement('button');
  playBtn.type = 'button';
  playBtn.className = 'card__play';
  playBtn.textContent = '▶ INSERT COIN';
  playBtn.addEventListener('click', () => launch(entry));

  actions.appendChild(playBtn);

  if (!entry.scoreless) {
    const boardBtn = document.createElement('button');
    boardBtn.type = 'button';
    boardBtn.className = 'card__board';
    boardBtn.textContent = 'TOP 10';
    boardBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      openLeaderboard(entry);
    });
    actions.appendChild(boardBtn);
  }

  card.append(band, scene, title, genre, blurb, actions);
  return card;
}

function launch(entry: CatalogEntry): void {
  track('cartridge_selected', { cartridgeId: entry.id, remastered: true });
  beginLaunch(entry);
}

/**
 * The single latched entry point for every launch (sc-7064). Acquires the
 * `launching` latch SYNCHRONOUSLY — a second call while a launch is in
 * flight (session active OR gate pending) is a no-op, so a double-click
 * can't fire two gates / spend two tokens. All launch triggers route
 * through here: the card button, the in-place email-OTP relaunch, and the
 * post-callback relaunchPending.
 */
function beginLaunch(entry: CatalogEntry): void {
  if (activeSession || launching) return; // one cartridge at a time
  launching = true;
  void gateThenLaunch(entry).catch(() => {
    // Defensive: resolvePlayAccess is written not to throw, but never let
    // an unexpected rejection strand the latch (which would wedge the
    // cabinet — no further launches possible).
    launching = false;
  });
}

/**
 * The entry gate (sc-6940): resolve play access, THEN mount — or show the
 * login wall / paywall. A signed-in token play spends the token at run
 * start (sc-7036). Assumes the `launching` latch is already held (set by
 * beginLaunch); releases it on every non-play terminal branch, and hands
 * it to startSession on 'play' (session ownership → activeSession).
 */
async function gateThenLaunch(entry: CatalogEntry): Promise<void> {
  const verdict = await resolvePlayAccess();
  switch (verdict.grant) {
    case 'play':
      // access tells the session whether a token must be spent at run
      // START (sc-7036): 'token' ⇒ spend on onStarted; 'pass'/'free-taste'
      // ⇒ no token spend (a pass is unlimited; the anon taste was already
      // consumed at the gate). startSession takes over the latch.
      startSession(entry, verdict.access);
      break;
    case 'login':
      track('login_wall_shown', { cartridgeId: entry.id });
      // Wall is up — the launch attempt is over; release the latch so a
      // dismiss-and-retry works. onSignedIn (in-place email-OTP, sc-7039)
      // re-enters through beginLaunch, re-acquiring cleanly. Google/magic-
      // link leave the page and resume via /auth/callback + relaunchPending.
      launching = false;
      void showLoginWall(entry.id, entry.title, () => {
        beginLaunch(entry);
      });
      break;
    case 'paywall':
      track('paywall_shown', { cartridgeId: entry.id });
      launching = false;
      // Pass the cartridge so a purchase relaunches it via /billing/return.
      // A rewarded token earned in the wall resolves tokenEarned → relaunch
      // the run right here (the run's own start-spend consumes the token).
      void showPaywall(verdict.resetsAtUtc, entry.id).then((r) => {
        if (paywallGrantsPlay(r)) beginLaunch(entry);
      });
      break;
    case 'error':
      // Server said no (fail-closed) — never a free run. Treat as paywall
      // so the player sees "try again shortly" rather than a silent nothing.
      launching = false;
      void showPaywall(undefined, entry.id).then((r) => {
        if (paywallGrantsPlay(r)) beginLaunch(entry);
      });
      break;
  }
}

function startSession(entry: CatalogEntry, access: 'pass' | 'token' | 'free-taste'): void {
  // Latch handoff (sc-7064): beginLaunch holds `launching` across the async
  // gate; here the session becomes the guard. Set activeSession first, then
  // release the latch — never a window where both are false while a run
  // exists.
  if (activeSession) {
    launching = false;
    return;
  }
  // Reflect the mounted cartridge in the URL (sc-8150). Set here rather
  // than at the gate so the address bar only ever advertises a cartridge
  // that actually mounted — a login wall or paywall leaves the URL alone.
  markDeepLink(entry.id);
  activeSession = launchGame(entry, {
    // Only a 'token' run spends at start (sc-7036). A pass is unlimited;
    // the anon free-taste was already consumed at the gate.
    spendTokenOnStart: access === 'token',
    onClose: () => {
      activeSession = null;
      // Back to the gallery — the URL should say so, or a reload would
      // relaunch the cartridge the player just quit.
      clearDeepLink();
      // play counts changed — let the lineup re-rank while the player
      // is looking at the gallery anyway
      renderGrid();
      // a token run just ended (or a pass lapsed) — repaint the footer
      // credit so the count reflects the spend.
      void refreshFooterCredits();
    },
  });
  launching = false;
}

/**
 * Auto-relaunch after an auth round-trip (sc-6940). main.ts calls this on
 * boot when the callback signed the player in and a pending cartridge was
 * stashed before the redirect. The player lands back mid-flow, in the game
 * they were trying to coin.
 */
export function relaunchPending(): void {
  const id = consumePendingLaunch();
  if (!id) return;
  const entry = CATALOG.find((c) => c.id === id);
  if (entry) beginLaunch(entry);
}

/**
 * Launch straight into `?game=<id>` on boot (sc-8150) — the inbound-link
 * entry point, so a per-cartridge post lands IN the game rather than on
 * the gallery.
 *
 * Routes through the same latched `beginLaunch`, so a deep link is gated
 * exactly like a card click: the free taste is consumed, the login wall
 * and paywall still apply, and a dev-gated id was already filtered out of
 * CATALOG by the production build. Returns whether a launch was started,
 * so main.ts can skip it when an auth/billing relaunch already owns the
 * boot (relaunchPending wins — that player was mid-flow).
 */
export function launchDeepLink(): boolean {
  const entry = pendingDeepLink();
  if (!entry) return false;
  track('deep_link_launch', { cartridgeId: entry.id, remastered: true });
  beginLaunch(entry);
  return true;
}
